/* MOURIN — theme.js : global UI (header, dialogs, reveal, search, small elements) */
(() => {
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (window.theme && window.theme.animations && !reducedMotion.matches) root.classList.add('anim');

  /* ---------- Utilities ---------- */
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };
  const announce = (message) => {
    const region = document.getElementById('A11yLiveRegion');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 60);
  };

  /* ---------- Dialogs (drawers, modals) ----------
     Native <dialog> gives focus trapping, inert background and Escape handling.
     We add animated open/close and backdrop click. */
  const Dialogs = {
    lastOpener: new WeakMap(),
    open(dialog, opener) {
      if (!dialog || dialog.open) return;
      if (opener) this.lastOpener.set(dialog, opener);
      document.querySelectorAll('dialog[open]').forEach((d) => { if (d !== dialog) this.close(d, true); });
      dialog.showModal();
      requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
      document.querySelectorAll(`[aria-controls="${dialog.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      dialog.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
    },
    close(dialog, instant = false) {
      if (!dialog || !dialog.open) return;
      dialog.classList.remove('is-open');
      document.querySelectorAll(`[aria-controls="${dialog.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      const finish = () => {
        dialog.close();
        dialog.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
        const opener = this.lastOpener.get(dialog);
        if (opener && document.contains(opener) && !instant) opener.focus({ preventScroll: true });
      };
      if (instant || reducedMotion.matches) return finish();
      let done = false;
      const onEnd = (e) => { if (e.target === dialog) end(); };
      const end = () => {
        if (done) return;
        done = true;
        dialog.removeEventListener('transitionend', onEnd);
        finish();
      };
      dialog.addEventListener('transitionend', onEnd);
      window.setTimeout(end, 650);
    }
  };
  window.MourinDialogs = Dialogs;

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('data-open-dialog'));
      if (dialog) {
        event.preventDefault();
        Dialogs.open(dialog, opener);
        return;
      }
    }
    const closer = event.target.closest('[data-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) { event.preventDefault(); Dialogs.close(dialog); }
      return;
    }
    if (event.target.tagName === 'DIALOG' && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside || event.target.classList.contains('lightbox')) Dialogs.close(event.target);
    }
  });

  document.addEventListener('cancel', (event) => {
    if (event.target.tagName === 'DIALOG') { event.preventDefault(); Dialogs.close(event.target); }
  }, true);

  /* ---------- Header ---------- */
  const setHeaderHeight = () => {
    const header = document.querySelector('.site-header');
    if (header) root.style.setProperty('--header-h', `${header.offsetHeight}px`);
  };

  const initHeader = () => {
    setHeaderHeight();
    const header = document.querySelector('.site-header');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    onScroll();
    if (!header.dataset.bound) {
      header.dataset.bound = 'true';
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  };

  window.addEventListener('resize', debounce(setHeaderHeight, 150));

  /* ---------- Reveal on scroll ---------- */
  let revealObserver;
  const initReveal = (scope = document) => {
    const items = scope.querySelectorAll('[data-reveal]:not(.is-visible), [data-reveal-section]:not(.is-visible)');
    if (!root.classList.contains('anim') || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    }
    items.forEach((el) => revealObserver.observe(el));
  };

  /* ---------- Announcement bar ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.announcement__item')];
      this.index = 0;
      if (this.items.length === 0) return;
      this.items[0].classList.add('is-active');
      this.speed = parseInt(this.dataset.speed || '5', 10) * 1000;
      if (this.items.length > 1 && !reducedMotion.matches) this.play();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.stop());
    }
    disconnectedCallback() { this.stop(); }
    play() {
      if (this.items.length < 2 || reducedMotion.matches) return;
      this.stop();
      this.timer = window.setInterval(() => this.show(this.index + 1), this.speed);
    }
    stop() { window.clearInterval(this.timer); }
    show(i) {
      this.items[this.index].classList.remove('is-active');
      this.index = (i + this.items.length) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
  }
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button[name]');
        if (!btn) return;
        e.preventDefault();
        const step = parseInt(this.input.step || '1', 10);
        const min = parseInt(this.input.min || '0', 10);
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const current = parseInt(this.input.value || '0', 10);
        const next = btn.name === 'plus' ? current + step : current - step;
        this.input.value = Math.max(min, Math.min(max, next));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this.update();
      });
      this.input.addEventListener('change', () => this.update());
      this.update();
    }
    update() {
      const min = parseInt(this.input.min || '0', 10);
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      const value = parseInt(this.input.value || '0', 10);
      const minus = this.querySelector('button[name="minus"]');
      const plus = this.querySelector('button[name="plus"]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = value >= max;
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.cache = new Map();
      if (!this.input || !this.results || this.dataset.enabled !== 'true') return;
      this.input.addEventListener('input', debounce(() => this.search(), 260));
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
    }
    async search() {
      const term = this.input.value.trim();
      if (!term) { this.results.innerHTML = ''; return; }
      if (this.cache.has(term)) { this.render(this.cache.get(term)); return; }
      this.controller?.abort();
      this.controller = new AbortController();
      const url = `${window.theme.routes.predictiveSearch}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=product,collection,page,article,query&resources[limit]=4&resources[limit_scope]=each`;
      try {
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const markup = new DOMParser().parseFromString(html, 'text/html').querySelector('#shopify-section-predictive-search');
        const content = markup ? markup.innerHTML : '';
        this.cache.set(term, content);
        this.render(content);
      } catch (err) {
        if (err.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
    render(content) {
      this.results.innerHTML = content;
      const count = this.results.querySelectorAll('[role="option"]').length;
      this.input.setAttribute('aria-expanded', count > 0 ? 'true' : 'false');
    }
    onKeydown(e) {
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      const options = [...this.results.querySelectorAll('[role="option"]')];
      if (!options.length) return;
      const current = options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      if (e.key === 'Enter') {
        if (current > -1) { e.preventDefault(); const link = options[current].querySelector('a'); if (link) link.click(); }
        return;
      }
      e.preventDefault();
      const next = e.key === 'ArrowDown' ? (current + 1) % options.length : (current - 1 + options.length) % options.length;
      options.forEach((o, i) => o.setAttribute('aria-selected', i === next ? 'true' : 'false'));
      this.input.setAttribute('aria-activedescendant', options[next].id);
      options[next].scrollIntoView({ block: 'nearest' });
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  document.addEventListener('dialog:open', (e) => {
    if (e.target.id === 'SearchModal') {
      const input = e.target.querySelector('input[type="search"]');
      window.setTimeout(() => input && input.focus(), 50);
    }
  });

  /* ---------- Auto-submit forms (facets, sort, localization) ---------- */
  document.addEventListener('change', (e) => {
    const form = e.target.closest('form[data-auto-submit]');
    if (!form) return;
    if (e.target.matches('input[type="number"]')) return;
    form.requestSubmit ? form.requestSubmit() : form.submit();
  });
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.matches('form[data-clean-empty]')) return;
    form.querySelectorAll('input[type="number"]').forEach((input) => { if (input.value === '') input.disabled = true; });
  });

  /* ---------- Share button ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share]');
    if (!btn) return;
    e.preventDefault();
    const data = { title: btn.dataset.title || document.title, url: btn.dataset.url || window.location.href };
    try {
      if (navigator.share) await navigator.share(data);
      else { await navigator.clipboard.writeText(data.url); announce(btn.dataset.copied || 'OK'); btn.textContent = btn.dataset.copied || btn.textContent; }
    } catch (_) { /* user cancelled */ }
  });

  /* ---------- Custom cursor (desktop, fine pointer only) ---------- */
  const initCursor = () => {
    const cursor = document.querySelector('.cursor');
    if (!cursor || !window.matchMedia('(hover: hover) and (pointer: fine)').matches || reducedMotion.matches) return;
    let x = -100, y = -100, raf;
    const render = () => { cursor.style.setProperty('--cx', `${x}px`); cursor.style.setProperty('--cy', `${y}px`); raf = null; };
    window.addEventListener('pointermove', (e) => { x = e.clientX; y = e.clientY; if (!raf) raf = requestAnimationFrame(render); }, { passive: true });
    document.addEventListener('pointerover', (e) => {
      cursor.classList.toggle('is-hover', !!e.target.closest('a, button, label, summary, [role="button"], input, select'));
    });
    document.addEventListener('pointerleave', () => cursor.classList.add('is-hidden'));
    document.addEventListener('pointerenter', () => cursor.classList.remove('is-hidden'));
  };

  /* ---------- Video: pause offscreen background videos ---------- */
  const initVideos = (scope = document) => {
    const videos = scope.querySelectorAll('[data-bg-video] video');
    if (!videos.length) return;
    if (reducedMotion.matches) { videos.forEach((v) => { v.removeAttribute('autoplay'); v.pause(); }); return; }
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) { const p = target.play(); if (p && p.catch) p.catch(() => {}); } else target.pause();
      });
    }, { threshold: 0.05 });
    videos.forEach((v) => { v.muted = true; v.setAttribute('playsinline', ''); io.observe(v); });
  };

  /* ---------- Init + Theme editor lifecycle ---------- */
  const init = () => {
    initHeader();
    initReveal();
    initCursor();
    initVideos();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', (e) => {
    initHeader();
    initVideos(e.target);
    e.target.querySelectorAll('[data-reveal], [data-reveal-section]').forEach((el) => el.classList.add('is-visible'));
  });
  document.addEventListener('shopify:section:select', (e) => {
    e.target.querySelectorAll('[data-reveal], [data-reveal-section]').forEach((el) => el.classList.add('is-visible'));
    if (e.target.querySelector('.site-header')) setHeaderHeight();
  });
  document.addEventListener('shopify:section:unload', () => window.setTimeout(setHeaderHeight, 50));
  document.addEventListener('shopify:block:select', (e) => {
    const bar = e.target.closest('announcement-bar');
    if (bar && bar.items) { bar.stop(); bar.show(bar.items.indexOf(e.target)); }
    e.target.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-visible'));
    if (e.target.matches('[data-reveal]')) e.target.classList.add('is-visible');
    if (e.target.matches('details')) e.target.open = true;
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const bar = e.target.closest('announcement-bar');
    if (bar && bar.play) bar.play();
  });

  window.Mourin = Object.assign(window.Mourin || {}, { debounce, announce, initReveal });
})();
